import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ViajeDTO } from '../../../dtos/viaje.dto';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { MovilDTO } from '../../../dtos/movil.dto';
import { MovilService } from '../../services/movil.service';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { Subject, Subscription, debounceTime, distinctUntilChanged, finalize, retry } from 'rxjs';
import { ViajeService } from '../../services/viaje.service';
import { AutoRefreshService } from '../../services/auto-refresh.service';
import { AppMessage } from '../../app.message';
import { ToastrService } from 'ngx-toastr';
import { EventService } from '../../services/event.service';
import { UserService } from '../../services/user.service';

// Constante para el estado de viaje en curso
const ESTADO_VIAJE_EN_CURSO = 'EN_CURSO';

// CON-1004: estados en los que no se permite editar el viaje (asignar/seleccionar)
const ESTADOS_VIAJE_NO_EDITABLE = ['EN_CURSO', 'NO_INICIADO', 'FINALIZADO'];

@Component({
  selector: 'app-bus',
  standalone: true,
  imports: [CommonModule, FormsModule, InfiniteScrollDirective],
  templateUrl: './bus.component.html',
  styleUrl: './bus.component.scss'
})
export class BusComponent implements OnInit, OnDestroy /*OnChanges*/ {

  @Output() viewChange = new EventEmitter<{ type: string, viajeSeleccionado: ViajeDTO | null }>();

  viajeSeleccionado: ViajeDTO;
  suscriberViajeSeleccionado: Subscription;

  tipoBusqueda = 'COMPLETA';

  asignandoMovil : boolean = false;

  // Getter para usar en el template
  get isViajeEnCurso(): boolean {
    return this.viajeSeleccionado?.estado === ESTADO_VIAJE_EN_CURSO;
  }

  // CON-1004: bloquea edición si el viaje está EN_CURSO, NO_INICIADO o FINALIZADO
  get isEdicionBloqueada(): boolean {
    return ESTADOS_VIAJE_NO_EDITABLE.includes(this.viajeSeleccionado?.estado);
  }

  buses: MovilDTO[] = [];
  filteredBuses: MovilDTO[] = [];
  loading: boolean = false;
  error: string | null = null;
  selectedBus: MovilDTO | null = null;
  selectedIndex: number = -1;
  searchText: string = '';
  showReemplazo: boolean = true; // Default to true to match the initial HTML state
  responseMsg: string = '';

  // Sorting variables
  orderBy: string = 'numeroInterno';
  orderAsc: boolean = true;

  // Infinite scroll variables
  private pageSize: number = 50;
  public page: number = 0;
  private hasNext: boolean = true;
  public desactivarInfiniteScroll: boolean = false;
  private isLoadingSort: boolean = false;
  private searchSubject = new Subject<string>();

  private autoRefreshSubscription: Subscription;
  private viajeActualizadoMqttSubscription: Subscription;
  private movilCambiadoDeSucursalSubscription : Subscription;

  constructor(
    private apiService: ApiService,
    private movilService: MovilService,
    private viajeService: ViajeService,
    private autoRefreshService: AutoRefreshService,
    private toastr: ToastrService,
    private eventService: EventService,
    private userService: UserService
  ) { }

  ngOnInit(): void {

    this.suscriberViajeSeleccionado = this.viajeService.viajeSeleccionadoDespachador$
      .pipe(distinctUntilChanged((a, b) => a?.uuid === b?.uuid))
      .subscribe(viajeSeleccionado => {
        this.viajeSeleccionado = viajeSeleccionado;
        if (this.viajeSeleccionado) {
          this.getListaMoviles(true);
        }
      });

    // Configure search debounce
    this.searchSubject.pipe(
      debounceTime(500), // Wait for 500ms after the last event
      distinctUntilChanged() // Only emit if value is different from previous
    ).subscribe(searchText => {
      this.searchText = searchText;
      this.getListaMoviles(true); // Reset and load with search text
    });

    // Suscribirse al evento de auto-refresh del contador
    this.autoRefreshSubscription = this.autoRefreshService.refresh$.subscribe(() => {
      console.log('Bus component: recibiendo evento de auto-refresh');
      if (this.viajeSeleccionado) {
        this.getListaMoviles(true);
      }
    });

    // Suscribirse al evento MQTT de viaje actualizado
    this.viajeActualizadoMqttSubscription = this.eventService.subscribe('VIAJE_ACTUALIZADO_MQTT_EVENT', (viaje) => {
      if (this.viajeSeleccionado.uuid === viaje.uuid) {
        // Actualizar el viaje seleccionado con la nueva información
        this.viajeSeleccionado = { ...viaje };
        // Refresh the bus list if needed
        this.getListaMoviles(true);
      }
    });

    this.movilCambiadoDeSucursalSubscription = this.eventService.subscribe('MOVIL_CAMBIADO_SUCURSAL_EVENT', () => {
      this.getListaMoviles(true);
    }); 
  }

  ngOnDestroy(): void {
    this.suscriberViajeSeleccionado.unsubscribe();
    if (this.autoRefreshSubscription) {
      this.autoRefreshSubscription.unsubscribe();
    }
    if (this.viajeActualizadoMqttSubscription) {
      this.viajeActualizadoMqttSubscription.unsubscribe();
    }
    if(this.movilCambiadoDeSucursalSubscription){
      this.movilCambiadoDeSucursalSubscription.unsubscribe();
    }
  }

  getListaMoviles(reset: boolean = false): void {
    this.loading = true;
    this.error = null;

    if (reset) {
      this.page = 0;
      this.hasNext = true;
      if (!this.isLoadingSort) {
        this.buses = [];
        this.filteredBuses = [];
      }
    }

    if (!this.viajeSeleccionado?.sucursalOrigen?.uuid
    ) {
      this.toastr.warning('El viaje no tienen terminal de origen', 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
      return;
    }

    this.apiService.getListaMovilesDisponibles(
      this.page,
      this.pageSize,
      this.searchText,
      this.viajeSeleccionado?.sucursalOrigen?.uuid,
      this.orderBy,
      this.orderAsc,
      'DISPONIBLE,EN_LAVADO,EN_CARGA',
      this.tipoBusqueda,
    )
    .subscribe({
      next: (response) => {
        const newBuses = response.data.list;

        if (response.msg) {
          this.responseMsg = response.msg;
        }

        this.hasNext = newBuses.length === this.pageSize;
        this.desactivarInfiniteScroll = !this.hasNext;

        this.buses = reset ? newBuses : [...this.buses, ...newBuses];
        this.filteredBuses = this.buses;
        this.loading = false;
        this.isLoadingSort = false;

        if (reset && this.filteredBuses.length > 0) {
          this.selectBus(this.filteredBuses[0], 0);
        } else if (this.filteredBuses.length === 0) {
          this.selectedBus = null;
          this.selectedIndex = -1;
        }

        console.log('Updated buses list', {
          page: this.page,
          totalCount: this.buses.length,
          filteredCount: this.filteredBuses.length,
          hasNext: this.hasNext
        });
      },
      error: (err) => {
        console.error('Error loading buses:', err);
        this.error = 'Error al cargar los buses. Por favor, intente nuevamente.';
        this.loading = false;
        this.isLoadingSort = false;
        this.desactivarInfiniteScroll = true;
        this.toastr.error((err.error?.msg || '') + (err['requestId'] ? ' Code: ' + err['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    });
  }

  onScroll(): void {
    if (!this.loading && this.hasNext) {
      console.log('Loading more buses...', { currentPage: this.page });
      this.page++;
      this.getListaMoviles();
    } else {
      console.log('Scroll event ignored', {
        loading: this.loading,
        hasNext: this.hasNext,
        page: this.page,
        reason: this.loading ? 'Currently loading' : 'No more data'
      });
    }
  }

  onSearchChange(text: string): void {
    this.searchSubject.next(text);
  }

  onSort(field: string): void {
    if (this.orderBy === field) {
      this.orderAsc = !this.orderAsc;
    } else {
      this.orderBy = field;
      this.orderAsc = true;
    }
    // Set loading flag to keep current data visible while sorting
    this.isLoadingSort = true;
    this.getListaMoviles(true);
  }


  selectBus(bus: MovilDTO, index: number): void {
    // Si el viaje no es editable (en curso, no iniciado o finalizado), no permitir la selección
    if (this.isEdicionBloqueada) {
      return;
    }

    // Si el bus no está disponible, no permitir la selección
    if (bus.estado !== 'DISPONIBLE') {
      return;
    }

    this.selectedBus = bus;
    this.selectedIndex = index;

    // Store the selected bus in the service for potential use in other components
    this.movilService.setMovilSeleccionado(bus);
  }

  viewBusDetails(bus: MovilDTO, event: Event): void {
    event.stopPropagation();

    // Siempre permitir ver detalles, incluso si el viajeSeleccionado está en curso
    const index = this.buses.findIndex(b => b === bus);
    if (index !== -1) {
      // Si el viajeSeleccionado está en curso, solo actualizamos la UI sin cambiar la selección
      if (this.isEdicionBloqueada) {
        this.selectedBus = bus;
        this.selectedIndex = index;
      } else {
        this.selectBus(bus, index);
      }
    }

    console.log('View details for bus:', bus);
  }

  closeReemplazo(): void {
    this.showReemplazo = false;
    console.log('Close replacement section');
  }

  toggleReemplazo(): void {
    this.showReemplazo = !this.showReemplazo;
    console.log('Toggle replacement section:', this.showReemplazo);
  }

  tienePermisoAsignarMovil(): boolean {
    return this.userService.tienePermiso('VIAJE_CAMBIAR_MOVIL');
  }

  asignarMovil(): void {
    // Si el viaje no es editable (en curso, no iniciado o finalizado), no permitir la asignación
    if (this.isEdicionBloqueada) {
      return;
    }

    if (!this.selectedBus || !this.viajeSeleccionado) {
      return;
    }

    this.asignandoMovil = true;
    this.apiService.putCambiarBus(this.viajeSeleccionado.uuid, this.selectedBus.uuid)
    .pipe(
      finalize(() => {
        this.asignandoMovil = false;
        this.getListaMoviles();
      })
    )
    .subscribe({
      next: (response) => {
        if (response.success) {
          this.toastr.success(response.msg, '', { timeOut: environment.toastSuccessTimeout, extendedTimeOut: environment.toastSuccessTimeout, closeButton: true, tapToDismiss: false });
          this.viajeSeleccionado.movil = this.selectedBus;
          //CON-1057: cambiar el bus anula la confirmacion en servicios, asi que el viaje vuelve a
          //pedir "Confirmar & Imprimir" y deja de ofrecer la reimpresion del vale
          this.viajeSeleccionado.confirmado = false;
        } else {
          this.toastr.warning(response.msg || 'Error al asignar el bus', 'Aviso', { timeOut: environment.toastWarningTimeout, extendedTimeOut: environment.toastWarningTimeout, closeButton: true, tapToDismiss: false });
        }
      },
      error: (error) => {
        console.log('error asignar movil', error);
        const msg = error.error && error.error.msg ? error.error.msg : AppMessage.ERROR_PUT_ASIGNAR_MOVIL_DESPACHADOR_WS;
        this.toastr.error(msg + (error['requestId'] ? ' Code: ' + error['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    });
  }

  /**
   * Refreshes only the page containing the specified bus
   * @param busUuid UUID of the bus to find and refresh its page
   */
  refreshPageContainingBus(busUuid: string): void {
    // Calculate which page the bus is on based on its index in the array
    const busIndex = this.buses.findIndex(bus => bus.uuid === busUuid);

    if (busIndex === -1) {
      console.log('Bus not found in the current list, no refresh needed');
      return;
    }

    // Calculate the page number (0-based) where the bus is located
    const pageNumber = Math.floor(busIndex / this.pageSize);
    console.log(`Bus found at index ${busIndex}, on page ${pageNumber}`);
    this.refreshSpecificPage(pageNumber);
  }

  /**
   * Refreshes a specific page of the bus list
   * @param pageNumber The page number to refresh (0-based)
   */
  refreshSpecificPage(pageNumber: number): void {
    this.loading = true;
    this.error = null;

    console.log(`Refreshing page ${pageNumber} of the bus list`);
    this.apiService.getListaMovilesDisponibles(
      pageNumber,
      this.pageSize,
      this.searchText,
      this.viajeSeleccionado.sucursalOrigen.uuid || '',
      this.orderBy,
      this.orderAsc
    ).subscribe({
      next: (response) => {
        const updatedBuses = response.data.list;

        // Calculate the start index for this page in the full array
        const startIndex = pageNumber * this.pageSize;

        // Replace only the items for this page in the full array
        for (let i = 0; i < updatedBuses.length; i++) {
          const fullArrayIndex = startIndex + i;

          // Make sure we don't go out of bounds
          if (fullArrayIndex < this.buses.length) {
            this.buses[fullArrayIndex] = updatedBuses[i];
          }
        }
        this.filteredBuses = this.buses;
        this.loading = false;

        console.log(`Page ${pageNumber} refreshed successfully`);
      },
      error: (err) => {
        console.error(`Error refreshing page ${pageNumber}:`, err);
        this.error = 'Error al actualizar la lista de buses. Por favor, intente nuevamente.';
        this.loading = false;
        this.toastr.error((err.error?.msg || '') + (err['requestId'] ? ' Code: ' + err['requestId'] : ''), 'Error', { timeOut: environment.toastErrorTimeout, extendedTimeOut: environment.toastErrorTimeout, closeButton: true, tapToDismiss: false });
      }
    });
  }

  getEnergiaDisponible(movil: MovilDTO) {
    if (!movil || !movil.tipoMovil || !movil.tipoMovil.combustible) {
      return '--';
    }
  
    if (movil.tipoMovil.combustible === 'DIESEL') {
      if (movil.nivelCombustible == null) {
        return 'Diesel / --%';
      }
      return `Diesel / ${movil.nivelCombustible}%`;
    }
  
    if (movil.tipoMovil.combustible === 'ELECTRICO') {
      if (movil.estadoCarga == null) {
        return 'Electr / --%';
      }
      return `Electr / ${movil.estadoCarga}%`;
    }
  
    return '--';
  }

  /**
   * Navega al componente de viajeSeleccionado cuando se hace clic en el encabezado
   */
  navigateToViaje(): void {
    if (this.viajeSeleccionado) {
      this.viewChange.emit({ type: 'viaje', viajeSeleccionado: this.viajeSeleccionado });
    }
  }
}
